const TARGET_FOLDER = "00_Skaliert";
const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i;

export interface LocalFile {
  name: string;
  size: number;
  handle: FileSystemFileHandle;
}

export interface LocalFolderRef {
  name: string;
  handle: FileSystemDirectoryHandle;
}

export function isFileSystemAccessSupported(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

type PermissionCapableHandle = FileSystemDirectoryHandle & {
  queryPermission?: (d: { mode: "read" | "readwrite" }) => Promise<PermissionState>;
  requestPermission?: (d: { mode: "read" | "readwrite" }) => Promise<PermissionState>;
};

// Stellt Schreibzugriff sicher. Scheitert das (Berechtigung verweigert oder ein
// vom Browser gesperrter Ordner wie Schreibtisch/Dokumente/geschützte Cloud-Ordner),
// gibt es statt der kryptischen Browser-Meldung einen klaren Hinweis.
async function ensureReadWritePermission(handle: FileSystemDirectoryHandle): Promise<void> {
  const h = handle as PermissionCapableHandle;
  const opts = { mode: "readwrite" as const };
  let state: PermissionState | undefined;
  try {
    if ((await h.queryPermission?.(opts)) === "granted") return;
    state = await h.requestPermission?.(opts);
  } catch {
    // Kontext konnte die Berechtigung nicht abfragen (z. B. eingebettetes
    // Vorschaufenster) – NICHT hart blockieren; der Schreibversuch entscheidet.
    return;
  }
  // Nur bei ausdrücklicher Ablehnung abbrechen. "prompt"/unbekannt → weiterversuchen.
  if (state === "denied") {
    throw new Error(
      "Kein Schreibzugriff auf diesen Ordner. Bitte beim Auswählen „Änderungen zulassen“ bestätigen. " +
        "Manche Ordner sind vom Browser gesperrt (z. B. Schreibtisch, Dokumente, Downloads oder geschützte Cloud-Ordner) – " +
        "wähle in dem Fall einen normalen Unterordner.",
    );
  }
}

export async function pickLocalFolder(): Promise<LocalFolderRef> {
  if (!isFileSystemAccessSupported()) {
    throw new Error(
      "Dein Browser unterstützt das Auswählen lokaler Ordner nicht. Bitte Chrome oder Edge nutzen.",
    );
  }
  // @ts-expect-error – showDirectoryPicker ist (noch) nicht im offiziellen TS-Lib
  const handle: FileSystemDirectoryHandle = await window.showDirectoryPicker({
    mode: "readwrite",
  });
  await ensureReadWritePermission(handle);
  return { name: handle.name, handle };
}

// Diagnose zum, was der Browser im gewählten Ordner tatsächlich sieht – hilft,
// leere Ergebnisse zu erklären (Unterordner, nur-online-Dateien, Richtlinie …).
export interface FolderDiagnostics {
  totalEntries: number;
  subfolderCount: number;
  fileCount: number;
  imageCount: number;
  unreadable: { name: string; error: string }[];
  sampleSubfolders: string[];
  sampleOtherFiles: string[];
}

export interface FolderScan {
  files: LocalFile[];
  diagnostics: FolderDiagnostics;
}

export async function scanLocalImages(folder: LocalFolderRef): Promise<FolderScan> {
  const files: LocalFile[] = [];
  const diag: FolderDiagnostics = {
    totalEntries: 0,
    subfolderCount: 0,
    fileCount: 0,
    imageCount: 0,
    unreadable: [],
    sampleSubfolders: [],
    sampleOtherFiles: [],
  };

  // @ts-expect-error – AsyncIterator-Typen für FileSystemDirectoryHandle noch nicht in Lib
  for await (const [name, child] of folder.handle.entries()) {
    diag.totalEntries += 1;
    if (child.kind === "directory") {
      diag.subfolderCount += 1;
      if (diag.sampleSubfolders.length < 5) diag.sampleSubfolders.push(name);
      continue;
    }
    diag.fileCount += 1;
    if (!IMAGE_EXTENSIONS.test(name)) {
      if (diag.sampleOtherFiles.length < 5) diag.sampleOtherFiles.push(name);
      continue;
    }
    diag.imageCount += 1;
    try {
      // getFile() scheitert typischerweise bei nur-online (OneDrive) Dateien oder
      // wenn die File System Access API per Browser-Richtlinie eingeschränkt ist.
      const file = await (child as FileSystemFileHandle).getFile();
      files.push({ name, size: file.size, handle: child as FileSystemFileHandle });
    } catch (err) {
      diag.unreadable.push({
        name,
        error: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      });
    }
  }

  // Zusätzlich in der Browser-Konsole ausgeben (für Weitergabe an Kollegin).
  console.info("[Skalierung] Ordner-Diagnose", diag);
  return { files, diagnostics: diag };
}

export async function ensureScaledSubfolder(
  folder: LocalFolderRef,
): Promise<FileSystemDirectoryHandle> {
  return await folder.handle.getDirectoryHandle(TARGET_FOLDER, { create: true });
}

export async function readLocalFile(file: LocalFile): Promise<ArrayBuffer> {
  const f = await file.handle.getFile();
  return await f.arrayBuffer();
}

export async function writeScaledFile(
  scaledFolder: FileSystemDirectoryHandle,
  fileName: string,
  data: ArrayBuffer,
): Promise<void> {
  const fileHandle = await scaledFolder.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(data);
  await writable.close();
}
